import { Uuid } from "@/domains/global/identifiers";
import {
  AudioRecordingRecordingStatus,
  AudioRecordingUploadStatus,
} from "@/store/audio-recordings/types";
import { AppStore } from "@/store/AppStore";
import { SourceObservable } from "@/store/sources/SourceObservable";

export enum AudioSourceAvailabilityState {
  Ready = "ready",
  Recording = "recording",
  Uploading = "uploading",
  Skipped = "skipped",
  Error = "error",
}

export type AudioSourceAvailability = {
  state: AudioSourceAvailabilityState;
  audioRecordingId: Uuid | null;
};

export const getAudioSourceAvailability = ({
  store,
  source,
}: {
  store: AppStore;
  source: SourceObservable;
}): AudioSourceAvailability => {
  if (source.sourceKind !== "AUDIO") {
    return { state: AudioSourceAvailabilityState.Ready, audioRecordingId: null };
  }

  const audioRecordingId = source.associatedAudioRecordingId ?? null;
  if (!audioRecordingId) {
    return { state: AudioSourceAvailabilityState.Ready, audioRecordingId: null };
  }

  const recording = store.audioRecordings.get(audioRecordingId);
  if (!recording) {
    return { state: AudioSourceAvailabilityState.Ready, audioRecordingId };
  }

  if (recording.isTrashed) {
    return { state: AudioSourceAvailabilityState.Ready, audioRecordingId };
  }

  if (recording.recordingStatus === AudioRecordingRecordingStatus.ERROR) {
    return { state: AudioSourceAvailabilityState.Error, audioRecordingId };
  }

  if (recording.uploadStatus === AudioRecordingUploadStatus.ERROR) {
    return { state: AudioSourceAvailabilityState.Error, audioRecordingId };
  }

  if (recording.uploadStatus === AudioRecordingUploadStatus.SKIPPED) {
    return { state: AudioSourceAvailabilityState.Skipped, audioRecordingId };
  }

  if (recording.recordingStatus === AudioRecordingRecordingStatus.IN_PROGRESS) {
    return { state: AudioSourceAvailabilityState.Recording, audioRecordingId };
  }

  if (recording.uploadStatus !== AudioRecordingUploadStatus.COMPLETED) {
    return { state: AudioSourceAvailabilityState.Uploading, audioRecordingId };
  }

  return { state: AudioSourceAvailabilityState.Ready, audioRecordingId };
};
